/**
 * TxnFormModal — universal Add/Edit modal for transactions (Кирим/Чиқим).
 *
 * Wraps `PopupModal` (project modal contract — never raw modal-bg). The form
 * is an AntD `Form` keyed off `Form.useForm`; per-field rules validate as the
 * user types and the full payload re-parses through `CreateTransactionSchema`
 * at submit (ACL boundary, never trust the form serializer alone).
 *
 * Field order mirrors the prototype `ExpenseModal`:
 *   1. Type toggle (Чиқим / Кирим)               — Segmented
 *   2. Сана · Валюта · Сумма                      — 3-col
 *   3. Категория chips                            — filtered by type, auto-clear on flip
 *   4. Тўлов усули cards                          — 4-col
 *   5. Контрагент · Боғлиқ ҳужжат                — 2-col
 *   6. Бўлим                                       — Select
 *   7. Изоҳ                                        — Textarea
 *   8. Footer: [Бекор қилиш][Сақлаш]
 *
 * Chip/card class strings come from `model/category-meta.ts` (single source,
 * drift-tested against `taxonomy.ts`). No inline `style` props anywhere.
 *
 * Create vs Edit:
 *   - `mode.kind === 'create'`: empty form, type defaults from `mode.type`,
 *     date defaults to today.
 *   - `mode.kind === 'edit'`: form prefilled from `mode.txn` (date → Dayjs);
 *     the MSW PUT handler refuses non-UNPAID rows (returns 409) — toast
 *     surfaces via `onError` from the mutation hook.
 *
 * Modal close timing: `onSuccess` (server-confirmed). The OPTIMISTIC cache
 * update — added in the next P2 batch on `mutations.ts` — happens on
 * `onMutate` so the row appears in the table immediately; the form itself
 * stays open with its Save button in `loading` state until the server
 * confirms or rejects. Best of both: optimistic table UX + safe form close.
 */

import {
  CreateTransactionSchema,
  type CategoryId,
  type CreateTransaction,
  type Currency,
  type Department,
  type PaymentMethod,
  type Transaction,
  type TxnType,
} from '@/features/accounting/api/contracts'
import { useCreateTransaction, useUpdateTransaction } from '@/features/accounting/api/mutations'
import { DEPARTMENT_META, categoriesByType } from '@/features/accounting/api/taxonomy'
import { toISODate } from '@/features/accounting/lib/date'
import { CATEGORY_META, CHIP_UNSELECTED_CLASS } from '@/features/accounting/model/category-meta'
import { dayjs } from '@/shared/lib/dayjs'
import { Button } from '@/shared/ui/atoms/button'
import { Checkbox } from '@/shared/ui/atoms/checkbox'
import { Input, InputNumberField } from '@/shared/ui/atoms/input'
import { RequiredMark } from '@/shared/ui/atoms/required-mark'
import { Select } from '@/shared/ui/atoms/select'
import { Textarea } from '@/shared/ui/atoms/textarea'
import { Datepicker } from '@/shared/ui/molecules/datepicker'
import { PopupModal } from '@/shared/ui/molecules/popup-modal'
import { SegmentedTabs, type SegmentedTabOption } from '@/shared/ui/molecules/segmented-tabs'
import { Toast } from '@/shared/ui/organisms/toast'
import { Form } from 'antd'
import type { Dayjs } from 'dayjs'
import { CreditCard, Landmark, Tv, Wallet } from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'
import { useEffect, useId, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export type TxnFormMode = { kind: 'create'; type: TxnType } | { kind: 'edit'; txn: Transaction }

interface TxnFormModalProps {
  open: boolean
  mode: TxnFormMode | null
  onClose: () => void
  onSaved?: () => void
}

interface FormValues {
  type: TxnType
  date: Dayjs
  currency: Currency
  // InputNumberField uses AntD `stringMode` for precision — onChange yields a
  // string. Validator + payload builder coerce back to number on submit.
  amount: number | string | null
  categoryId: CategoryId | null
  paymentMethod: PaymentMethod
  partyName: string
  sourceRef: string
  department: Department
  note: string
  // UI-only — backend DTO will own this once the API ships. For now it stays
  // in the form so the checkbox state survives create/edit round-trips.
  paid: boolean
}

// Payment-method ENUM rendered as a card row. `labelKey` and `hintKey` resolve
// through i18n so the same constant drives uzCYR/uzLAT/ruRUS without code
// changes. Icons come from `lucide-react` (already a project dep) and match
// the target screenshot one-to-one — Wallet for cash, Landmark for bank
// transfer, CreditCard for plastic, Tv for the FX kiosk/screen affordance.
type LucideIcon = ComponentType<{ size?: number | string; className?: string }>
const PAYMENT_METHODS: ReadonlyArray<{
  id: PaymentMethod
  labelKey: string
  hintKey: string
  Icon: LucideIcon
}> = [
  {
    id: 'CASH',
    labelKey: 'payment_method.CASH',
    hintKey: 'payment_method_hint.CASH',
    Icon: Wallet,
  },
  {
    id: 'TRANSFER',
    labelKey: 'payment_method.TRANSFER',
    hintKey: 'payment_method_hint.TRANSFER',
    Icon: Landmark,
  },
  {
    id: 'CARD',
    labelKey: 'payment_method.CARD',
    hintKey: 'payment_method_hint.CARD',
    Icon: CreditCard,
  },
  { id: 'FX', labelKey: 'payment_method.FX', hintKey: 'payment_method_hint.FX', Icon: Tv },
]

function TrendingUpIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <polyline points='3 17 9 11 13 15 21 7' />
      <polyline points='14 7 21 7 21 14' />
    </svg>
  )
}

function TrendingDownIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <polyline points='3 7 9 13 13 9 21 17' />
      <polyline points='14 17 21 17 21 10' />
    </svg>
  )
}

// Type toggle tabs — hoisted to module scope so the icon ReactElements are
// allocated once. `iconClassName` colours the inactive svg; the active state
// is repainted white via `.segmented-tab--pill[aria-selected='true'] svg`.
interface TypeTab {
  value: TxnType
  labelKey: string
  iconClassName: string
  icon: ReactNode
}
const TYPE_TABS: ReadonlyArray<TypeTab> = [
  {
    value: 'INCOME',
    labelKey: 'tab.income',
    iconClassName: 'text-(--text-success)',
    icon: <TrendingUpIcon />,
  },
  {
    value: 'EXPENSE',
    labelKey: 'tab.expense',
    iconClassName: 'text-(--text-error)',
    icon: <TrendingDownIcon />,
  },
]

const DEFAULT_VALUES: FormValues = {
  type: 'EXPENSE',
  date: dayjs(),
  currency: 'UZS',
  amount: null,
  categoryId: null,
  paymentMethod: 'CASH',
  partyName: '',
  sourceRef: '',
  department: 'SUPPLY',
  note: '',
  paid: true,
}

function txnToFormValues(txn: Transaction): FormValues {
  return {
    type: txn.type,
    date: dayjs(txn.date),
    currency: txn.currency,
    amount: txn.amount,
    categoryId: txn.categoryId,
    paymentMethod: txn.paymentMethod,
    partyName: txn.partyName,
    sourceRef: txn.sourceRef ?? '',
    department: txn.department,
    note: txn.note,
    // Existing rows treat anything non-UNPAID as already paid; UNPAID rows
    // come back unchecked so the user can confirm payment on save.
    paid: txn.status !== 'UNPAID',
  }
}

function buildPayload(values: FormValues): unknown {
  return {
    type: values.type,
    date: toISODate(values.date),
    currency: values.currency,
    amount: typeof values.amount === 'string' ? Number(values.amount) : values.amount,
    categoryId: values.categoryId,
    paymentMethod: values.paymentMethod,
    partyId: null,
    partyName: values.partyName.trim(),
    sourceRef: values.sourceRef.trim() === '' ? null : values.sourceRef.trim(),
    department: values.department,
    note: values.note,
    // Forwarded for the eventual backend DTO; the current MSW handler ignores
    // unknown fields so this is a safe no-op until the real API lands.
    paid: values.paid,
  }
}

// ─── Category chips ───────────────────────────────────────────────────────────
// AntD `Form.Item` clones its child with `value` + `onChange` injected; the
// remaining props (`type`, `translate`) come from the JSX call-site and ride
// the clone untouched. No Field-wrapper indirection needed.

interface CategoryChipsProps {
  value?: CategoryId | null
  onChange?: (id: CategoryId) => void
  type: TxnType
  translate: (key: string) => string
}

function CategoryChips({ value, onChange, type, translate }: CategoryChipsProps) {
  const options = categoriesByType(type)
  const groupName = useId()
  return (
    <div
      role='radiogroup'
      aria-label={translate('form.field_category')}
      className='flex flex-wrap gap-2'
    >
      {options.map((cat) => {
        const isSelected = value === cat.id
        const toneClass = isSelected
          ? CATEGORY_META[cat.id].chipSelectedClass
          : CHIP_UNSELECTED_CLASS
        return (
          <label
            key={cat.id}
            className={[
              'inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors',
              'has-focus-visible:ring-2 has-focus-visible:ring-(--primary-btn-bg) has-focus-visible:ring-offset-1',
              toneClass,
              isSelected ? 'font-semibold' : 'font-medium',
            ].join(' ')}
          >
            <input
              type='radio'
              name={groupName}
              value={cat.id}
              checked={isSelected}
              onChange={() => onChange?.(cat.id)}
              className='sr-only'
            />
            <span
              aria-hidden='true'
              className={[
                'inline-block h-2.5 w-2.5 rounded-sm',
                CATEGORY_META[cat.id].swatchClass,
              ].join(' ')}
            />
            {translate(cat.labelKey)}
          </label>
        )
      })}
    </div>
  )
}

// ─── Payment method cards ─────────────────────────────────────────────────────

interface PaymentMethodCardsProps {
  value?: PaymentMethod
  onChange?: (id: PaymentMethod) => void
  translate: (key: string) => string
}

function PaymentMethodCards({ value, onChange, translate }: PaymentMethodCardsProps) {
  const current = value ?? 'CASH'
  const groupName = useId()
  return (
    <div
      role='radiogroup'
      aria-label={translate('form.field_method')}
      className='grid grid-cols-2 gap-2 md:grid-cols-4'
    >
      {PAYMENT_METHODS.map((method) => {
        const isSelected = current === method.id
        const Icon = method.Icon
        return (
          <label
            key={method.id}
            className={[
              'flex  cursor-pointer items-center gap-3 rounded-md border px-3 py-2.5 text-left transition-colors',
              'has-focus-visible:ring-2 has-focus-visible:ring-(--primary-btn-bg) has-focus-visible:ring-offset-1',
              isSelected
                ? 'border-(--primary-btn-bg) bg-[#1a1c3b0a]'
                : 'border-(--input-border) bg-white hover:bg-[#f6f8fa]',
            ].join(' ')}
          >
            <input
              type='radio'
              name={groupName}
              value={method.id}
              checked={isSelected}
              onChange={() => onChange?.(method.id)}
              className='sr-only'
            />
            <Icon size={16} className='shrink-0 text-(--text-default)' aria-hidden='true' />
            <span className='flex min-w-0 flex-col leading-tight'>
              <span
                className={[
                  'truncate text-sm text-(--text-default)',
                  isSelected ? 'font-semibold' : 'font-medium',
                ].join(' ')}
              >
                {translate(method.labelKey)}
              </span>
              <span className='truncate text-xs text-(--text-secondary)'>
                {translate(method.hintKey)}
              </span>
            </span>
          </label>
        )
      })}
    </div>
  )
}

// ─── Main modal ───────────────────────────────────────────────────────────────

export function TxnFormModal({ open, mode, onClose, onSaved }: TxnFormModalProps) {
  const { t } = useTranslation('accounting')
  const [form] = Form.useForm<FormValues>()
  const createMutation = useCreateTransaction()
  const updateMutation = useUpdateTransaction()
  const isPending = createMutation.isPending || updateMutation.isPending

  // Reset the form whenever the modal opens with a new mode. `open` guards the
  // reset so closing the modal does not wipe values that are still in flight.
  useEffect(() => {
    if (!open || !mode) return
    if (mode.kind === 'create') {
      form.setFieldsValue({ ...DEFAULT_VALUES, type: mode.type })
    } else {
      form.setFieldsValue(txnToFormValues(mode.txn))
    }
  }, [open, mode, form])

  // Watch `type` to drive the category chip filter + auto-clear category on
  // type flip so an EXPENSE chip cannot stay selected after switching to INCOME.
  const watchedType = (Form.useWatch('type', form) as TxnType | undefined) ?? 'EXPENSE'
  const watchedCategory = Form.useWatch('categoryId', form) as CategoryId | null | undefined

  useEffect(() => {
    if (!watchedCategory) return
    const allowed = categoriesByType(watchedType).some((c) => c.id === watchedCategory)
    if (!allowed) form.setFieldValue('categoryId', null)
  }, [watchedType, watchedCategory, form])

  const handleFinish = (values: FormValues) => {
    const payload = buildPayload(values)
    const parsed = CreateTransactionSchema.safeParse(payload)
    if (!parsed.success) {
      Toast({ type: 'error', message: t('errors.contract_violation') })
      return
    }
    const body = parsed.data as CreateTransaction
    if (mode?.kind === 'create') {
      createMutation.mutate(body, {
        onSuccess: () => {
          onSaved?.()
          onClose()
        },
      })
    } else if (mode?.kind === 'edit') {
      updateMutation.mutate(
        { id: mode.txn.id, body },
        {
          onSuccess: () => {
            onSaved?.()
            onClose()
          },
        },
      )
    }
  }

  const title =
    mode?.kind === 'edit'
      ? t('form.title_edit_txn')
      : watchedType === 'INCOME'
        ? t('form.title_create_txn_income')
        : t('form.title_create_txn_expense')

  // Type-aware contraparty label: an INCOME row records the *payer* (the
  // customer who paid us), while an EXPENSE row records the *payee* (the
  // supplier we paid). Same form field, two labels and two placeholders so
  // the screen reader and the input affordance both stay accurate.
  const partyLabel = watchedType === 'INCOME' ? t('form.field_payer') : t('form.field_payee')
  const partyPlaceholder =
    watchedType === 'INCOME' ? t('form.field_payer_placeholder') : t('form.field_payee_placeholder')

  const requiredMessage = t('errors.required_field')
  const amountPositiveMessage = t('errors.amount_positive')

  const titleNode = (
    <div className='flex flex-col gap-1'>
      <span className='text-base font-semibold text-(--text-default)'>{title}</span>
      <span className='text-sm font-normal text-(--text-secondary)'>
        {t('form.modal_subtitle')}
      </span>
    </div>
  )

  // typeTabOptions is memoised against `t` so labels are resolved only on
  // locale change, not on every form re-render. Icon ReactElements come
  // straight from the module-level TYPE_TABS — referentially stable.
  const typeTabOptions: SegmentedTabOption<TxnType>[] = useMemo(
    () =>
      TYPE_TABS.map((tab) => ({
        value: tab.value,
        label: t(tab.labelKey),
        icon: tab.icon,
        iconClassName: tab.iconClassName,
      })),
    [t],
  )

  return (
    <PopupModal isModalOpen={open} onCancel={onClose} width='900px' title={titleNode}>
      <Form
        form={form}
        layout='vertical'
        onFinish={handleFinish}
        initialValues={DEFAULT_VALUES}
        className='flex flex-col gap-4'
        requiredMark={(labelNode, info) => (
          <>
            {labelNode}
            {info.required ? <RequiredMark /> : null}
          </>
        )}
      >
        <Form.Item name='type' label={t('form.field_type')} required>
          <SegmentedTabs<TxnType>
            variant='pill'
            options={typeTabOptions}
            value={watchedType}
            onChange={(value) => form.setFieldValue('type', value)}
            aria-label={t('form.field_type')}
          />
        </Form.Item>

        <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
          <Form.Item
            name='date'
            label={t('form.field_date')}
            rules={[{ required: true, message: requiredMessage }]}
          >
            <Datepicker
              format='DD MMM, YYYY'
              aria-label={t('form.field_date')}
              onChange={() => {}}
            />
          </Form.Item>
          <Form.Item name='currency' label={t('form.field_currency')} required>
            <Select
              aria-label={t('form.field_currency')}
              options={[
                { value: 'UZS', label: t('currency.UZS') },
                { value: 'USD', label: t('currency.USD') },
              ]}
            />
          </Form.Item>
          <Form.Item
            name='amount'
            label={t('form.field_amount')}
            rules={[
              { required: true, message: requiredMessage },
              {
                validator: async (_, value) => {
                  if (value == null || value === '') return
                  // InputNumberField uses AntD stringMode — value may be a string.
                  const num = typeof value === 'string' ? Number(value) : value
                  if (!Number.isFinite(num) || !Number.isInteger(num) || num < 1) {
                    throw new Error(amountPositiveMessage)
                  }
                },
              },
            ]}
          >
            <InputNumberField
              aria-label={t('form.field_amount')}
              min={0}
              step={1000}
              placeholder='0'
            />
          </Form.Item>
        </div>

        <Form.Item
          name='categoryId'
          label={t('form.field_category')}
          rules={[{ required: true, message: requiredMessage }]}
        >
          <CategoryChips type={watchedType} translate={t} />
        </Form.Item>

        <Form.Item name='paymentMethod' label={t('form.field_method')} required>
          <PaymentMethodCards translate={t} />
        </Form.Item>

        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <Form.Item
            name='partyName'
            label={partyLabel}
            rules={[{ required: true, message: requiredMessage }]}
          >
            <Input aria-label={partyLabel} placeholder={partyPlaceholder} maxLength={120} />
          </Form.Item>
          <Form.Item name='sourceRef' label={t('form.field_doc_ref')}>
            <Input
              aria-label={t('form.field_doc_ref')}
              placeholder={t('form.field_doc_ref_placeholder')}
              maxLength={60}
            />
          </Form.Item>
        </div>

        <Form.Item name='note' label={t('form.field_note')}>
          <Textarea
            aria-label={t('form.field_note')}
            placeholder={t('form.field_note_placeholder')}
            rows={3}
            maxLength={500}
          />
        </Form.Item>

        {/*
         * Department stays in the form state (the existing
         * CreateTransactionSchema + MSW handler still require it) but the
         * target screen drops it from the user-facing layout. Once the
         * backend DTO arrives the field can be removed or repurposed.
         */}
        <Form.Item name='department' hidden>
          <Select
            aria-label={t('form.field_department')}
            options={(Object.keys(DEPARTMENT_META) as Department[]).map((dept) => ({
              value: dept,
              label: t(DEPARTMENT_META[dept].labelKey),
            }))}
          />
        </Form.Item>

        <Form.Item name='paid' valuePropName='checked' className='m-0'>
          <Checkbox aria-label={t('form.field_paid_done')}>{t('form.field_paid_done')}</Checkbox>
        </Form.Item>

        <div className='flex items-center justify-end gap-2 pt-2'>
          <Button onClick={onClose} disabled={isPending}>
            {t('actions.cancel')}
          </Button>
          <Button type='primary' htmlType='submit' loading={isPending}>
            {t('actions.save')}
          </Button>
        </div>
      </Form>
    </PopupModal>
  )
}
